import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import zh from "@/i18n/locales/zh.json";
import en from "@/i18n/locales/en.json";

export const LANGUAGE_STORAGE_KEY = "oransim_v3_language";
export const supportedLanguages = ["zh", "en"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

function readLanguage(): SupportedLanguage {
  const queryLanguage = new URLSearchParams(window.location.search).get("lang");
  if (supportedLanguages.includes(queryLanguage as SupportedLanguage)) {
    return queryLanguage as SupportedLanguage;
  }
  try {
    const value = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (supportedLanguages.includes(value as SupportedLanguage)) {
      return value as SupportedLanguage;
    }
  } catch {
    // Storage can be unavailable in hardened browser contexts.
  }
  return "en";
}

const initialLanguage = readLanguage();

const PAGE_METADATA: Record<SupportedLanguage, { title: string; description: string }> = {
  en: {
    title: "OranSim · Causal Agent Marketing Forecasting",
    description: "OranSim causal agent platform for marketing outcome forecasting.",
  },
  zh: {
    title: "OranSim · 因果智能体营销效果预测",
    description: "OranSim 因果智能体营销效果预测平台",
  },
};

function syncDocumentLanguage(language: string): void {
  const supportedLanguage: SupportedLanguage = language.startsWith("zh") ? "zh" : "en";
  const metadata = PAGE_METADATA[supportedLanguage];
  document.documentElement.lang = supportedLanguage;
  document.title = metadata.title;
  document
    .querySelector<HTMLMetaElement>('meta[name="description"]')
    ?.setAttribute("content", metadata.description);
}

void i18n.use(initReactI18next).init({
  resources: {
    zh: { translation: zh },
    en: { translation: en },
  },
  lng: initialLanguage,
  fallbackLng: "zh",
  supportedLngs: [...supportedLanguages],
  interpolation: {
    escapeValue: false,
  },
});

i18n.on("languageChanged", (language) => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Ignore storage failures and keep the in-memory language.
  }
  syncDocumentLanguage(language);
});

syncDocumentLanguage(initialLanguage);

export default i18n;
